import { useTranslation } from "react-i18next";

import OpenArrow from "@/assets/ux/ArrowOpenDoc.svg?react";
import { Button } from "@/components/Interface/Button/Button";
import { TableRow } from "@/components/Layout/Table/TableRow/TableRow";
import { DrEvn } from "@/services/model";
import { FCC } from "@/types";
import { formatYearMonth } from "@/utils/dateHelpers";
import { getCurrency } from "@/utils/translationHelpers";

import { ItemizedBillCheckbox } from "./ItemizedBillCheckbox";

interface iItemizedBillOverviewRowProps {
  itemizedBill: DrEvn;
  downloadItemizedBill: ({
    serviceId,
    period,
  }: {
    serviceId: number;
    period: string;
  }) => void;
  variant?: "default" | "compact";
  isSelectMultiple?: boolean;
}

const DataRow: FCC = ({ children }) => (
  <td className="max-w-52 truncate text-left">{children}</td>
);

export const ItemizedBillOverviewRow = ({
  itemizedBill: { serviceId, contract, periode: period, amount, billNumber },
  downloadItemizedBill,
  variant = "default",
  isSelectMultiple,
}: iItemizedBillOverviewRowProps) => {
  const { i18n, t } = useTranslation("portal");

  if (!serviceId || !period) {
    return null;
  }

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-4 border-b border-border-tertiary py-4">
        {/* Leading slot, aligned with the select-all beside the results
            heading; the open button keeps its trailing place either way. */}
        {isSelectMultiple && (
          <div className="flex shrink-0 items-center">
            <ItemizedBillCheckbox serviceId={serviceId} period={period} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start">
            <span className="flex-1 break-words text-left text-body-base font-semibold text-foreground-primary">
              {contract}
            </span>
          </div>

          <div className="mt-1 flex gap-4 text-left text-body-base text-foreground-primary">
            <span>
              {formatYearMonth(period, i18n.language)} {billNumber}
            </span>
            <span className="shrink-0 text-body-base font-semibold text-foreground-primary">
              {getCurrency(amount || 0, "CHF")}
            </span>
          </div>
        </div>

        <Button
          dataTestid="mobile-itemized-bill-row-download"
          variant="secondary"
          size="sm"
          type="button"
          ariaLabel={t("bills.list.downloadBill")}
          icon={<OpenArrow className="size-4" />}
          onClick={() => downloadItemizedBill({ serviceId, period })}
        />
      </div>
    );
  }

  return (
    <>
      <TableRow>
        {isSelectMultiple && (
          <td className="evn-selection text-body-base leading-normal">
            <ItemizedBillCheckbox serviceId={serviceId} period={period} />
          </td>
        )}
        <DataRow>{contract}</DataRow>
        <DataRow>{formatYearMonth(period, i18n.language)}</DataRow>
        <DataRow>{getCurrency(amount || 0, "CHF")}</DataRow>
        <DataRow>{billNumber}</DataRow>
        <td>
          <Button
            dataTestid="itemized-bill-download-icon"
            variant="secondary"
            size="sm"
            type="button"
            ariaLabel={t("bills.list.downloadBill")}
            icon={<OpenArrow className="size-4" />}
            onClick={() => downloadItemizedBill({ serviceId, period })}
          />
        </td>
      </TableRow>
    </>
  );
};
